import { useState } from 'react';
import { T } from 'gt-react';
import { Button } from 'src/components/ui/button';
import { Checkbox } from 'src/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';

export interface BindableStep {
  stepId: string;
  name: string;
}

/** Choose which steps a guide frame shows (the screen as it looked before acting on them). */
export function SopGuideStepBindingDialog({
  open,
  onOpenChange,
  steps,
  selected,
  onSave,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  steps: BindableStep[];
  selected: string[];
  onSave: (stepIds: string[]) => void;
}) {
  const [chosen, setChosen] = useState<Set<string>>(new Set(selected));
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (next) setChosen(new Set(selected));
        onOpenChange(next);
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            <T context="title of the guide frame step-binding dialog">
              Which steps does this screen show?
            </T>
          </DialogTitle>
          <DialogDescription>
            <T context="help text in the guide frame step-binding dialog">
              Pick the steps the author performed on this screen. The agent is
              shown this frame when it works on them.
            </T>
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-[50vh] space-y-2 overflow-y-auto">
          {steps.map((step, index) => {
            const id = `bind-${step.stepId}`;
            return (
              <label
                key={step.stepId}
                htmlFor={id}
                className="flex cursor-pointer items-center gap-2 rounded border p-2 text-sm"
              >
                <Checkbox
                  id={id}
                  checked={chosen.has(step.stepId)}
                  onCheckedChange={(checked) =>
                    setChosen((prev) => {
                      const next = new Set(prev);
                      if (checked) next.add(step.stepId);
                      else next.delete(step.stepId);
                      return next;
                    })
                  }
                />
                <span className="font-mono text-xs text-muted-foreground">
                  {index + 1}
                </span>
                <span className="truncate">{step.name}</span>
              </label>
            );
          })}
        </div>
        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onOpenChange(false)}
          >
            <T context="cancel button">Cancel</T>
          </Button>
          <Button
            size="sm"
            onClick={() => {
              onSave(steps.map((s) => s.stepId).filter((id) => chosen.has(id)));
              onOpenChange(false);
            }}
          >
            <T context="save step bindings button">Save</T>
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
